/**
 * The landing page's pictures of the product, drawn in HTML rather than screenshots: sharp at any
 * size, a few kB, and they say exactly what the app does today. Names and numbers are fictional.
 */
import { CalendarDays, KanbanSquare, Link2, MessageSquare, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@client/lib/utils";

export const TABS = [
  { key: "conversas", label: "Conversas", icon: MessageSquare },
  { key: "comercial", label: "Comercial", icon: KanbanSquare },
  { key: "agenda", label: "Agenda", icon: CalendarDays },
] as const;
export type Tab = (typeof TABS)[number]["key"];

const bubble = "max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed";

export function ConversasMock() {
  return (
    <div className="l-tab grid h-full gap-4 md:grid-cols-[1fr_220px]">
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex items-center gap-3 border-b border-white/5 pb-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--l-accent)]/15 text-xs font-bold text-[var(--l-accent)]">MS</div>
          <div>
            <p className="text-sm font-bold text-white">Mariana Souza</p>
            <p className="text-[10px] text-[var(--l-muted)]">WhatsApp · Novo lead · veio pelo Instagram</p>
          </div>
        </div>
        <div className={cn(bubble, "self-start rounded-tl-sm bg-white/5 text-[var(--l-muted)]")}>
          Oi! Vi vocês no Instagram. Queria saber como funciona o acompanhamento?
        </div>
        <div className={cn(bubble, "self-start rounded-tl-sm bg-white/5 text-[var(--l-muted)]")}>
          Eu como muito à noite e queria emagrecer pro meu casamento em março.
        </div>
        <div className="mt-auto rounded-2xl border border-[var(--l-accent)]/25 bg-[var(--l-accent)]/[0.07] p-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--l-accent)]">
            <Sparkles className="h-3 w-3" /> Sugestão do assistente
          </p>
          <p className="text-xs leading-relaxed text-white">
            Oi, Mariana! Que bom que você chegou até nós. Entendi: comer muito à noite, e você quer
            chegar bem no casamento. Posso te fazer duas perguntinhas para te indicar o melhor formato?
          </p>
          <div className="mt-3 flex gap-2">
            <span className="rounded-full bg-[var(--l-accent)] px-3 py-1 text-[10px] font-bold text-[var(--l-on-accent)]">Aprovar e enviar</span>
            <span className="rounded-full border border-white/10 px-3 py-1 text-[10px] font-semibold text-white">Ajustar</span>
          </div>
        </div>
      </div>
      <aside className="hidden space-y-3 rounded-2xl border border-white/5 bg-white/[0.02] p-3 md:block">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--l-muted)]">Resumo</p>
        <div>
          <p className="text-[10px] text-[var(--l-muted)]">Dor</p>
          <p className="text-xs text-white">come muito à noite</p>
        </div>
        <div>
          <p className="text-[10px] text-[var(--l-muted)]">Objetivo</p>
          <p className="text-xs text-white">emagrecer para o casamento</p>
        </div>
        <p className="flex items-center gap-1 text-[10px] text-[var(--l-muted)]"><Sparkles className="h-3 w-3" /> anotado pelo assistente</p>
        <div className="border-t border-white/5 pt-3">
          <p className="text-[10px] text-[var(--l-muted)]">Próximo passo</p>
          <p className="text-xs text-white">Entender o objetivo · hoje</p>
        </div>
      </aside>
    </div>
  );
}

export function ComercialMock() {
  const cols = [
    { name: "Novo lead", cards: [{ n: "Mariana Souza", s: "Instagram", v: null, stale: false }, { n: "Rafael Lima", s: "Indicação", v: null, stale: false }] },
    { name: "Em conversa", cards: [{ n: "Beatriz Nunes", s: "Google", v: "R$ 250", stale: false }, { n: "Lucas Prado", s: "Instagram", v: "R$ 600", stale: true }] },
    { name: "Proposta enviada", cards: [{ n: "Camila Torres", s: "Link de agendamento", v: "R$ 600", stale: false }] },
  ];
  return (
    <div className="l-tab grid h-full grid-cols-1 gap-3 sm:grid-cols-3">
      {cols.map((c, i) => (
        <div key={c.name} className={cn("rounded-2xl bg-white/[0.02] p-3", i > 0 && "hidden sm:block")}>
          <p className="mb-3 flex items-center justify-between text-[11px] font-bold text-white">
            {c.name} <span className="font-normal text-[var(--l-muted)]">{c.cards.length}</span>
          </p>
          <div className="space-y-2">
            {c.cards.map((card) => (
              <div key={card.n} className={cn("rounded-xl border border-white/5 bg-white/[0.04] p-3",
                                              card.stale && "border-l-2 border-l-amber-400/70")}>
                <p className="text-xs font-bold text-white">{card.n}</p>
                <p className="mt-0.5 flex justify-between text-[10px] text-[var(--l-muted)]">
                  <span>{card.s}</span>{card.v && <span className="font-semibold text-white">{card.v}</span>}
                </p>
                {card.stale && <p className="mt-1 text-[10px] font-semibold text-amber-300/90">parada há muito tempo</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AgendaMock() {
  const days = ["Seg", "Ter", "Qua", "Qui", "Sex"];
  const appts: Record<string, { t: string; n: string; k: string }[]> = {
    Seg: [{ t: "09:00", n: "Ana Paula", k: "Retorno" }],
    Ter: [{ t: "14:00", n: "Camila Torres", k: "Primeira consulta" }],
    Qua: [{ t: "10:30", n: "Lucas Prado", k: "Online" }, { t: "16:00", n: "Beatriz Nunes", k: "Retorno" }],
    Qui: [],
    Sex: [{ t: "11:00", n: "Rafael Lima", k: "Conversa inicial" }],
  };
  return (
    <div className="l-tab grid h-full gap-4 md:grid-cols-[1fr_220px]">
      <div className="grid grid-cols-5 gap-2">
        {days.map((d) => (
          <div key={d} className="min-w-0 rounded-xl bg-white/[0.02] p-2">
            <p className="mb-2 text-center text-[10px] font-bold uppercase text-[var(--l-muted)]">{d}</p>
            <div className="space-y-1.5">
              {appts[d]!.map((a) => (
                <div key={a.t} className="rounded-lg border border-[var(--l-accent)]/15 bg-[var(--l-accent)]/[0.08] p-1.5">
                  <p className="text-[10px] font-bold text-[var(--l-accent)]">{a.t}</p>
                  <p className="truncate text-[10px] text-white">{a.n}</p>
                  <p className="hidden truncate text-[9px] text-[var(--l-muted)] sm:block">{a.k}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <aside className="hidden rounded-2xl border border-white/5 bg-white/[0.02] p-3 md:block">
        <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--l-muted)]">
          <Link2 className="h-3 w-3" /> Seu link de agendamento
        </p>
        <p className="break-all rounded-lg bg-white/5 px-2 py-1.5 text-[11px] text-white">chatnutri.com.br/<span className="text-[var(--l-accent)]">suanutri</span>/agendar</p>
        <p className="mt-3 text-[11px] leading-relaxed text-[var(--l-muted)]">
          O paciente escolhe um horário livre. Aparece aqui, no Google Agenda, no quadro e na conversa.
        </p>
      </aside>
    </div>
  );
}

// ─── The hero phone ──────────────────────────────────────────────────────────

/**
 * The moment that sells, on a loop: a message arrives, the assistant writes, you approve, it goes.
 * Pure CSS (styles/site.css, `l-p-*`); `playing` pauses it while it is off-screen.
 */
export function PhoneLoop({ playing, size = "lg" }: { playing: boolean; size?: "lg" | "md" }) {
  return (
    <div className={cn("l-phone relative mx-auto w-full", size === "lg" ? "max-w-[320px]" : "max-w-[236px]")} data-playing={playing}
         role="img" aria-label="Uma mensagem chega no WhatsApp, o assistente escreve a resposta, e você aprova e envia.">
      <div className="rounded-[2.6rem] border border-white/10 bg-[#0d1820] p-2.5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="overflow-hidden rounded-[2.1rem] bg-[#0b141a]" aria-hidden>
          <div className="flex items-center gap-2.5 bg-[#1f2c34] px-4 pb-3 pt-6">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--l-accent)]/20 text-[11px] font-bold text-[var(--l-accent)]">JR</div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-white">Júlia Ramos</p>
              <p className="text-[10px] text-[var(--l-muted)]">Novo lead · veio pelo Instagram</p>
            </div>
          </div>
          <div className={cn("flex flex-col gap-2 px-3 py-4", size === "lg" ? "min-h-[300px] sm:min-h-[380px]" : "min-h-[290px]")}>
            <div data-step="in" className="max-w-[85%] self-start rounded-xl rounded-tl-sm bg-[#1f2c34] px-3 py-2 text-[12.5px] leading-snug text-white">
              Oi! Quanto custa o acompanhamento? Queria perder uns 6 kg até o fim do ano 🙏
              <span className="mt-1 block text-right text-[9px] text-[var(--l-muted)]">21:47</span>
            </div>
            <div data-step="typing" className="flex items-center gap-1.5 self-start overflow-hidden rounded-full bg-[var(--l-accent)]/10 px-3 py-1.5 text-[10px] font-semibold text-[var(--l-accent)]">
              <Sparkles className="h-3 w-3" /> Assistente escrevendo…
            </div>
            <div data-step="draft" className="overflow-hidden rounded-xl border border-[var(--l-accent)]/30 bg-[var(--l-accent)]/[0.07] p-3">
              <p className="mb-1 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[var(--l-accent)]">
                <Sparkles className="h-2.5 w-2.5" /> Sugestão do assistente
              </p>
              <p className="text-[12px] leading-snug text-white">
                Oi, Júlia! Já te passo os valores. Antes, me conta: o que mais tem dificultado
                hoje para chegar nesses 6 kg?
              </p>
              <div className="mt-2.5 flex gap-1.5">
                <span data-step="approve" className="rounded-full bg-[var(--l-accent)] px-2.5 py-1 text-[10px] font-bold text-[var(--l-on-accent)]">Aprovar e enviar</span>
                <span className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-white">Ajustar</span>
              </div>
            </div>
            <div data-step="sent" className="max-w-[85%] self-end overflow-hidden rounded-xl rounded-tr-sm bg-[#005c4b] px-3 py-2 text-[12.5px] leading-snug text-white">
              Oi, Júlia! Já te passo os valores. Antes, me conta: o que mais tem dificultado
              hoje para chegar nesses 6 kg?
              <span className="mt-1 block text-right text-[9px] text-white/60">21:48 ✓✓</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── The journey's pictures ──────────────────────────────────────────────────

/** A small dark app window, so the product reads as the product on the light sections. */
export function Window({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div aria-hidden className={cn("rounded-2xl border border-black/5 bg-[var(--l-bg)] p-1.5 shadow-[0_30px_60px_-24px_rgba(9,20,29,0.45)]", className)}>
      <div className="rounded-xl bg-[var(--l-surface-low)] p-4 text-left">{children}</div>
    </div>
  );
}

const chip = "rounded-full px-2 py-0.5 text-[10px] font-semibold";

export function ArrivalsMock() {
  const rows = [
    { n: "Júlia Ramos", m: "Oi! Quanto custa o acompanhamento?", s: "Instagram", t: "21:47", unread: true },
    { n: "Pedro Alves", m: "A Carla me indicou você", s: "Indicação", t: "20:12", unread: true },
    { n: "Sofia Mendes", m: "Agendou pelo link · Ter 14:00", s: "Link de agendamento", t: "18:30", unread: false },
  ];
  return (
    <Window>
      <p className="mb-3 flex items-center justify-between text-[11px] font-bold text-white">
        Conversas <span className="text-[10px] font-semibold text-[var(--l-accent)]">3 novos leads no quadro</span>
      </p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.n} className="flex items-center gap-3 rounded-xl bg-white/[0.04] p-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--l-accent)]/15 text-[10px] font-bold text-[var(--l-accent)]">
              {r.n.split(" ").map((w) => w[0]).join("")}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex justify-between text-xs font-bold text-white">{r.n}<span className="text-[10px] font-normal text-[var(--l-muted)]">{r.t}</span></span>
              <span className="block truncate text-[11px] text-[var(--l-muted)]">{r.m}</span>
            </span>
            <span className={cn(chip, "hidden bg-white/5 text-[var(--l-muted)] sm:inline")}>{r.s}</span>
            {r.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--l-accent)]" />}
          </li>
        ))}
      </ul>
    </Window>
  );
}

export function ScriptMock() {
  return (
    <Window>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {["Primeira resposta", "Entender o objetivo", "Proposta", "Objeções", "Lembrete"].map((m, i) => (
          <span key={m} className={cn(chip, i === 1 ? "bg-[var(--l-accent)] text-[var(--l-on-accent)]" : "bg-white/5 text-[var(--l-muted)]")}>{m}</span>
        ))}
      </div>
      <div className="rounded-xl border border-[var(--l-accent)]/25 bg-[var(--l-accent)]/[0.07] p-3">
        <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--l-accent)]">
          <Sparkles className="h-3 w-3" /> Sugestão · Entender o objetivo
        </p>
        <p className="text-xs leading-relaxed text-white">
          Entendi, Júlia. Então o que mais pesa hoje é beliscar à noite, e você quer chegar ao fim do
          ano 6 kg mais leve. Faz sentido?
        </p>
        <p className="mt-2 text-[10px] text-[var(--l-muted)]">Adaptado do seu roteiro, com as palavras da pessoa.</p>
      </div>
    </Window>
  );
}

export function CloseMock() {
  return (
    <Window>
      <p className="mb-3 text-[11px] font-bold text-white">Em conversa</p>
      <div className="space-y-2">
        <div className="rounded-xl border border-white/5 border-l-2 border-l-amber-400/70 bg-white/[0.04] p-3">
          <p className="flex justify-between text-xs font-bold text-white">Lucas Prado <span>R$ 600</span></p>
          <p className="mt-0.5 text-[10px] font-semibold text-amber-300/90">parada há muito tempo</p>
          <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[10px] font-semibold text-white">
            <Sparkles className="h-3 w-3 text-[var(--l-accent)]" /> Sugerir follow-up
          </span>
        </div>
        <div className="rounded-xl border border-[var(--l-accent)]/20 bg-[var(--l-accent)]/[0.06] p-3">
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--l-accent)]">
            <Link2 className="h-3 w-3" /> Agendou pelo link
          </p>
          <p className="mt-1 text-xs text-white">Sofia Mendes · Primeira consulta · Ter 14:00</p>
          <p className="mt-0.5 text-[10px] text-[var(--l-muted)]">Na agenda, no Google e na conversa</p>
        </div>
      </div>
    </Window>
  );
}

export function RenewMock() {
  return (
    <Window>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-white">Beatriz Nunes</p>
        <span className={cn(chip, "bg-amber-400/15 text-amber-300")}>Renovação</span>
      </div>
      <p className="mt-0.5 text-[10px] text-[var(--l-muted)]">Paciente · Acompanhamento 3 meses</p>
      <div className="mt-3">
        <div className="mb-1 flex justify-between text-[10px] text-[var(--l-muted)]"><span>Plano</span><span>termina em 12 dias</span></div>
        <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full w-[87%] rounded-full bg-[var(--l-accent)]" /></div>
      </div>
      <div className="mt-3 rounded-xl border border-[var(--l-accent)]/25 bg-[var(--l-accent)]/[0.07] p-3">
        <p className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--l-accent)]">
          <Sparkles className="h-3 w-3" /> Mensagem de renovação pronta
        </p>
        <p className="text-xs leading-relaxed text-white">
          Oi, Bia! Faltam poucos dias para fechar o seu ciclo. Que tal a gente olhar o que evoluiu e
          planejar os próximos meses?
        </p>
      </div>
    </Window>
  );
}

// ─── "Você no controle" ──────────────────────────────────────────────────────

export function DraftCardMock() {
  return (
    <div aria-hidden className="l-glass rounded-2xl p-5 text-left">
      <p className="mb-3 text-[11px] text-[var(--l-muted)]">
        <span className="font-bold text-white">Paciente:</span> Posso comer pão à noite?
      </p>
      <div className="rounded-xl border border-[var(--l-accent)]/25 bg-[var(--l-accent)]/[0.07] p-4">
        <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--l-accent)]">
          <Sparkles className="h-3 w-3" /> Sugestão do assistente
        </p>
        <p className="text-sm leading-relaxed text-white">
          Boa pergunta! Isso eu prefiro ver com você na consulta, olhando a sua rotina. Quer que eu
          já veja um horário para esta semana?
        </p>
        <p className="mt-2 text-[10px] text-[var(--l-muted)]">Motivo: orientação alimentar é com você, na consulta.</p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="rounded-full bg-[var(--l-accent)] px-4 py-1.5 text-xs font-bold text-[var(--l-on-accent)]">Aprovar e enviar</span>
        <span className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-white">Ajustar</span>
        <span className="rounded-full px-4 py-1.5 text-xs font-semibold text-[var(--l-muted)]">Descartar</span>
      </div>
    </div>
  );
}
